import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './index.module.scss';
import { CategoryNode } from '../../types/category';
import { groupCategoriesIntoColumns } from '../../util/categoryUtils';
import { useHistory } from 'react-router-dom';
import { buildSearchListHistoryLocation } from '@/pages/SearchList/utils/searchListUrl';
import { getCategoryTree } from '../../api/newApi/header';
import { isArray } from 'lodash';
import { reportSearchButtonClick } from '@/dataPort/verticalSearchReport';
import { getUuid } from '@/util';
import { usePvId } from '@/util/hooks/usePvId';

interface CategoryDrawerProps {
  visible: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const INDUSTRIAL_CATEGORY_ID = 14065;

const normalizeCategoryTree = (tree: CategoryNode[] = []): CategoryNode[] => {
  const industrialCategory = tree.find((category: CategoryNode) => category.id === INDUSTRIAL_CATEGORY_ID);
  const children = industrialCategory?.children ?? [];
  if (!children.length) {
    return tree;
  }

  const restTopCategories = tree.filter((category: CategoryNode) => category.id !== INDUSTRIAL_CATEGORY_ID);
  return [...children, ...restTopCategories];
};

const CategoryDrawer: React.FC<CategoryDrawerProps> = ({ visible, onClose, onMouseEnter, onMouseLeave }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [categoriesError, setCategoriesError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [backdropTop, setBackdropTop] = useState<number>(0);
  const history = useHistory();
  const userInfo = useSelector((state: any) => state?.User?.data?.[0] ?? {});
  const uuid = getUuid();
  const { updatePvId } = usePvId({ autoInit: false });

  const fetchCategoryData = useCallback(async () => {
    if (categories.length > 0 || loading) {
      return;
    }

    setLoading(true);
    setCategoriesError(false);
    try {
      const response: any = await getCategoryTree({});
      if (response?.success) {
        const rawData = isArray(response?.value) ? response.value : [];
        const normalizedCategories = normalizeCategoryTree(rawData);
        setCategories(normalizedCategories);
        setSelectedCategoryId(currentId => currentId || normalizedCategories[0]?.id || null);
        return;
      }

      setCategoriesError(true);
    } catch (error) {
      setCategoriesError(true);
      console.error('获取类目数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [categories.length, loading]);

  useEffect(() => {
    if (visible) {
      fetchCategoryData();
    }
  }, [visible, fetchCategoryData]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
    };
  }, [visible]);

  const updateBackdropTop = useCallback(() => {
    const panelBottom = panelRef.current?.getBoundingClientRect().bottom ?? 0;
    const nextBackdropTop = Math.max(0, Math.round(panelBottom));
    setBackdropTop(currentBackdropTop =>
      currentBackdropTop === nextBackdropTop ? currentBackdropTop : nextBackdropTop,
    );
  }, []);

  useEffect(() => {
    if (!visible) {
      setBackdropTop(0);
      return undefined;
    }

    updateBackdropTop();
    const rafId = window.requestAnimationFrame(updateBackdropTop);
    const resizeObserver =
      typeof ResizeObserver === 'undefined' || !panelRef.current ? null : new ResizeObserver(updateBackdropTop);

    resizeObserver?.observe(panelRef.current);
    window.addEventListener('resize', updateBackdropTop);

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateBackdropTop);
    };
  }, [visible, loading, categories.length, selectedCategoryId, updateBackdropTop]);

  const selectedCategory = useMemo(
    () => categories.find(category => category.id === selectedCategoryId) || categories[0],
    [categories, selectedCategoryId],
  );

  const selectedColumns = useMemo(
    () => (selectedCategory ? groupCategoriesIntoColumns(selectedCategory) : [[], [], []]),
    [selectedCategory],
  );

  const reportCreateVerticalSearchCategoryClick = (categoryId: any) => {
    const { pvId, logId } = updatePvId('new_search');
    reportSearchButtonClick({
      keyword: categoryId,
      input_word: '',
      pin: userInfo?.pin,
      uuid,
      pvid: pvId,
      logid: logId,
      userType: userInfo?.userType === 5 ? 1 : 2,
      source: '2',
      timestamp: Date.now(),
      ext: '',
      contractid: userInfo?.contractNum,
    });
  };

  const openWin = (key: any, cataIdList: any, name: string) => {
    reportCreateVerticalSearchCategoryClick(key);
    onClose();
    history.replace(
      buildSearchListHistoryLocation(
        { key: '', cat: key, pageNo: 1, sort: 0 },
        { cataIdList: String(cataIdList), cat: key },
        { query: name, searchTriggerTs: Date.now() },
      ),
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <div ref={panelRef} className={styles.panel} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {loading ? (
          <div className={styles.panelContent}>
            <div className={styles.feedback}>{TLT('加载中')}...</div>
          </div>
        ) : categoriesError ? (
          <div className={styles.panelContent}>
            <div className={styles.feedback}>{TLT('类目加载异常')}...</div>
          </div>
        ) : categories.length > 0 ? (
          <div className={styles.panelContent}>
            <div className={styles.panelInner}>
              <div className={styles.leftMenu}>
                {categories.map(category => (
                  <button
                    key={category.id}
                    type="button"
                    className={`${styles.menuItem} ${
                      selectedCategory?.id === category.id ? styles.menuItemActive : ''
                    }`}
                    title={category.name}
                    onMouseEnter={() => setSelectedCategoryId(category.id)}
                    onClick={() => openWin(category.id, `${category.id}`, category.name)}
                  >
                    <span>{category.name}</span>
                    <i className={styles.menuItemArrow} aria-hidden="true" />
                  </button>
                ))}
              </div>

              <div className={styles.categoryContent}>
                {selectedColumns.some(column => column.length > 0) ? (
                  <div className={styles.contentGrid}>
                    {selectedColumns.map((column, columnIndex) => (
                      <div key={columnIndex} className={styles.column}>
                        {column.map(secondLevel => (
                          <div key={secondLevel.id} className={styles.categoryGroup}>
                            <button
                              type="button"
                              className={styles.groupTitle}
                              title={secondLevel.name}
                              onClick={() =>
                                openWin(secondLevel.id, `${selectedCategory?.id},${secondLevel.id}`, secondLevel.name)
                              }
                            >
                              {secondLevel.name}
                            </button>
                            <div className={styles.subItems}>
                              {secondLevel.children?.map(thirdLevel => (
                                <button
                                  key={thirdLevel.id}
                                  type="button"
                                  className={styles.subItem}
                                  title={thirdLevel.name}
                                  onClick={() =>
                                    openWin(
                                      thirdLevel.id,
                                      `${selectedCategory?.id},${secondLevel.id},${thirdLevel.id}`,
                                      thirdLevel.name,
                                    )
                                  }
                                >
                                  {thirdLevel.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.feedback}>{TLT('暂无类目')}...</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.panelContent}>
            <div className={styles.feedback}>{TLT('暂无类目')}...</div>
          </div>
        )}
      </div>
      <button
        type="button"
        className={`${styles.backdrop} ${backdropTop <= 0 ? styles.backdropHidden : ''}`}
        style={{ top: backdropTop }}
        aria-label={TLT('关闭分类面板')}
        onClick={onClose}
      />
    </>
  );
};

export default CategoryDrawer;
